import { createTheme } from '@mui/material/styles';
import { palette } from './palette';
import { typography } from './typography';
import { components } from './components';
import { breakpoints, brand, radii, shadows } from './tokens';

export { brand, colors, spacing, radii, shadows, motion, breakpoints, typography as typographyTokens } from './tokens';
export * from '@/theme/motion';

/** Softer radii for guide + admin dashboards (pre-boxy look). */
export const dashboardRadii = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  '2xl': '32px',
  full: '999px',
} as const;

/** CSS vars to restore soft corners inside dashboard layout trees. */
export const dashboardRadiusCssVars = {
  '--radius-xs': dashboardRadii.xs,
  '--radius-sm': dashboardRadii.sm,
  '--radius-md': dashboardRadii.md,
  '--radius-lg': dashboardRadii.lg,
  '--radius-xl': dashboardRadii.xl,
  '--radius-2xl': dashboardRadii['2xl'],
  '--radius-full': dashboardRadii.full,
} as const;

export const theme = createTheme({
  palette,
  typography,
  components,
  spacing: 4,
  shape: { borderRadius: 6 },
  breakpoints: {
    values: {
      xs: breakpoints.xs,
      sm: breakpoints.sm,
      md: breakpoints.md,
      lg: breakpoints.lg,
      xl: breakpoints.xl,
    },
  },
  customShadows: shadows,
  radii,
});

/** Guide/admin dashboards keep the previous rounded chrome. */
export const dashboardTheme = createTheme(theme, {
  shape: { borderRadius: 12 },
  radii: dashboardRadii,
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: dashboardRadii.md },
        sizeLarge: { borderRadius: dashboardRadii.lg },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: dashboardRadii.lg },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: dashboardRadii.lg },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': { borderRadius: dashboardRadii.md },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: dashboardRadii.md },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: { borderRadius: dashboardRadii.md },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: dashboardRadii.sm },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: dashboardRadii.md },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: dashboardRadii.lg },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: dashboardRadii.md },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { borderRadius: dashboardRadii.sm },
      },
    },
  },
});

export const authTheme = createTheme({
  ...theme,
  palette: {
    ...theme.palette,
    background: {
      default: brand.navy,
      paper: brand.white,
    },
  },
});
